<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="4"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_LOANS_AND_ADVANCES_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            text="Export"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              CSV
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              Excel
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="8"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <!-- Status -->
            <b-col md="3">
              <SelectField
                id="status"
                v-model="filter.reportType"
                label=""
                placeholder="Select"
                :options="accountStatusOptions"
                :set-selected="getReportType"
                item="label"
                :reduce="option => option.value"
              />
            </b-col>
            <!-- Status -->
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="loanAdvancesTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(linkedAccountBalance)="data">
        <p class="mb-0">
          {{ formatAmount(data.value) }}
        </p>
      </template>

      <template #cell(startDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(endDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(disbursedAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(refinanceAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(totalOutstandingAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(accruingInterestReceivable)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(lastRepaymentAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(principalDueAndUnpaid)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(principalNotYetDue)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(interestDueAndUnpaid)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(lastRepaymentDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>
      <template #cell(lastLodgmentDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(disbursementDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(installmentAmount)="data">
        <p class="mb-0">
          {{ data.value ? formatAmount(data.value) : '' }}
        </p>
      </template>
      <template #cell(loanStatus)="data">
        <div v-if="data.value">
          <b-badge
            pill
            :variant="getStatusColor(data.value)"
          >
            {{ data.value }}
          </b-badge>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      title="Filters"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="loanAdvanceRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                label="Account Number"
                placeholder="Enter account number"
                type="text"
              />
            </b-col>
            <!-- Account Number -->

            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                label="Account Name"
                placeholder="Enter account name"
                type="text"
              />
            </b-col>
            <!-- Account Name -->
            <!-- IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle-id"
                v-model="filter.employeeID"
                label="IPPIS/Oracle ID"
                placeholder="Enter IPPIS/Oracle ID"
                type="text"
              />
            </b-col>
            <!-- IPPIS/Oracle ID -->

            <b-col md="6 mt-2">
              <SwitchButton
                v-model="filter.useCurrentFinancialDate"
                label="Use Current Financial Date"
                name="use-current-financial-date"
                :readonly="isReadonly"
                @input="setUseCurrentFinancialDate(filter.useCurrentFinancialDate)"
              />
            </b-col>

            <!-- Financial Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date"
                v-model="filter.financialDate"
                :min-date="new Date()"
                label="Financial Date"
                :config="datePickerConfig"
                rules="required"
                error-name="Financial Date"
                placeholder="Select financial date"
                :disabled="isDisabledText"
              />
            </b-col>
            <!-- Financial Date -->

            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                label="Product Name"
                placeholder="Select"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->

            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                label="Branch"
                placeholder="Select"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <b-col md="6">
              <InputField
                id="employee-name"
                v-model="filter.employeeName"
                label="Employee Name"
                placeholder="Enter Employee Name"
                type="text"
              />
            </b-col>
            <b-col md="6">
              <DatePickerField
                id="disbursement-date-from"
                v-model="filter.disbursementDateFrom"
                label="Disbursement Date From"
                :config="datePickerConfigOthers"
                placeholder="Select date"
              />
            </b-col>
            <b-col md="6">
              <DatePickerField
                id="disbursement-date-to"
                v-model="filter.disbursementDateTo"
                label="Disbursement Date To"
                :config="datePickerConfigOthers"
                placeholder="Select date"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="filter.accountOfficerStaffId"
                label="Account Officer"
                :options="accountOfficerOptions"
                :reduce="option => option.value"
                item="label"
                placeholder="Select"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- modal -->
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner,
  BModal, BDropdown, BDropdownItem, BBadge, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import Ripple from 'vue-ripple-directive'
// import jsPDF from 'jspdf'
// import 'jspdf-autotable'
// import XLSX from 'xlsx'
import { heightFade } from '@core/directives/animations'
import { mapActions, mapGetters } from 'vuex'
import { datePickerConfig } from '@core/mixins/datePicker'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import * as FileSaver from 'file-saver'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import getStatusColor from '../../../../../helpers/getStatusColor'
import toNumber from '../../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate } from '../../../../../helpers/formatDate'
import SwitchButton from '../../../../components/switch/Switch.vue'
import Button from '../../../../components/button/Button.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import { reportPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BForm,
    InputField,
    BModal,
    BBadge,
    BDropdown,
    BDropdownItem,
    TablePagination,
    TableEntries,
    SelectField,
    DatePickerField,
    SwitchButton,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, datePickerConfig, sweetAlert, permissionMixin],
  data() {
    return {
      isDisabledText: false,
      query: '',
      getStatusColor,
      statusToggleLabel: '',
      isReadonly: false,
      isDisabled: true,
      buttonLabel: 'Save',
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: 'Loan and Advances Report',
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account No' },
        { key: 'accountName', sortable: true },
        { key: 'linkedAccount', sortable: true },
        { key: 'linkedAccountBalance', sortable: true },
        { key: 'accountOfficer', sortable: true },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'branch', sortable: true },
        { key: 'product', sortable: true },
        { key: 'productCategory', sortable: true },
        { key: 'economicSector', sortable: true },
        { key: 'lendingModel', sortable: true },
        { key: 'tenure', sortable: true },
        { key: 'startDate', sortable: true },
        { key: 'endDate', sortable: true },
        { key: 'disbursedAmount', sortable: true },
        { key: 'disbursementDate', sortable: true },
        { key: 'refinanceAmount', sortable: true },
        { key: 'interestRate', sortable: true, label: 'Int. Rate (PA)' },
        { key: 'defaultingLoanInterest', sortable: true, label: 'Defaulting Loan Interest' },
        { key: 'totalOutstandingAmount', sortable: true, label: 'Total Outstanding Principal' },
        { key: 'principalDueAndUnpaid', sortable: true, label: 'Principal Due & Unpaid' },
        { key: 'principalNotYetDue', sortable: true },
        { key: 'interestDueAndUnpaid', sortable: true, label: 'Interest Due & Unpaid' },
        { key: 'accruingInterestReceivable', sortable: true },
        { key: 'daysPastDue', sortable: true },
        { key: 'lastRepaymentAmount', sortable: true, label: 'Last Repaid Amount' },
        { key: 'installmentAmount', sortable: true },
        { key: 'employerName', sortable: true },
        { key: 'employerAddress', sortable: true },
        { key: 'lastLodgmentDate', sortable: true, label: 'Last Lodgment Date' },
        { key: 'lastRepaymentDate', sortable: true, label: 'Last Repaid Date' },
        { key: 'phoneNumber', sortable: true, label: 'Cust. Phone No' },
        { key: 'gender', sortable: true },
        { key: 'guarantorName', sortable: true },
        { key: 'guarantorPhoneNumber', sortable: true },
        { key: 'loanStatus', sortable: true, label: 'Status' },
      ],
      filter: {
        accountNumber: '',
        accountName: '',
        financialDate: null,
        productCode: '',
        branchCode: '',
        employeeID: '',
        employerName: '',
        accountOfficerStaffId: '',
        disbursementDateFrom: null,
        disbursementDateTo: null,
        useCurrentFinancialDate: true,
        reportType: 1,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      datePickerConfigOthers: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      accountStatusOptions: [
        { value: 1, label: 'Loan' },
        { value: 2, label: 'Overdraft' },
      ],
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      productOptions: [],
      accountOfficerOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Account Number': item.accountNumber,
        'Account Name': item.accountName,
        'Linked Account': item.linkedAccount,
        'Linked Account Balance': item.linkedAccountBalance ? this.toAmount(item.linkedAccountBalance) : '',
        'Account Officer': item.accountOfficer,
        'IPPIS/Oracle ID': item.employeeID,
        Branch: item.branch,
        Product: item.product,
        Tenure: item.tenure,
        'Start Date': item.startDate ? this.formatCustomDate(item.startDate) : '',
        'End Date': item.endDate ? this.formatCustomDate(item.endDate) : '',
        'Disbursed Amount': item.disbursedAmount ? this.toAmount(item.disbursedAmount) : '',
        'Disbursement Date': item.disbursementDate ? this.formatCustomDate(item.disbursementDate) : '',
        'Refinance Amount': item.refinanceAmount ? this.toAmount(item.refinanceAmount) : '',
        'Interest Rate': item.interestRate,
        'Defaulting Loan Interest': item.defaultingLoanInterest,
        'Total Outstanding Principal': item.totalOutstandingAmount ? this.toAmount(item.totalOutstandingAmount) : '',
        'Principal Due And Unpaid': item.principalDueAndUnpaid ? this.toAmount(item.principalDueAndUnpaid) : '',
        'Principal Not Yet Due': item.principalNotYetDue ? this.toAmount(item.principalNotYetDue) : '',
        'Interest Due And Unpaid': item.interestDueAndUnpaid ? this.toAmount(item.interestDueAndUnpaid) : '',
        'Accruing Interest Receivable': item.accruingInterestReceivable ? this.toAmount(item.accruingInterestReceivable) : '',
        'Days Past Due': item.daysPastDue,
        'Last Repaid Amount': item.lastRepaymentAmount ? this.toAmount(item.lastRepaymentAmount) : '',
        'Installment Amount': item.installmentAmount ? this.toAmount(item.installmentAmount) : '',
        'Last Lodgment Date': item.lastLodgmentDate ? this.formatCustomDate(item.lastLodgmentDate) : '',
        'Last Repaid Date': item.lastRepaymentDate ? this.formatCustomDate(item.lastRepaymentDate) : '',
        'Employee Name': item.employerName,
        'Employee Address': item.employerAddress,
        'Cust. Phone No.': item.phoneNumber,
        gender: item.gender,
        'Guarantor Name': item.guarantorName,
        'Guarantor Phone': item.guarantorPhoneNumber,
        'Loan Status': item.loanStatus,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchLoanAndAdvances()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchLoanAndAdvances()
    },
  },
  mounted() {
    this.fetchProduct()
    this.fetchLoanAndAdvances()
    this.fetchAccountOfficers()
  },
  methods: {
    ...mapActions({
      getLoanAdvances: 'reports/getLoanAdvances',
      downloadLoanAdvances: 'reports/downloadLoanAdvances',
      getProductByTypes: 'account/getProductByTypes',
      getBranchByCode: 'operations/getBranchByCode',
      getAccountOfficers: 'account/getAccountOfficerList',
    }),

    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_AND_ADVANCES_REPORT,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
      this.fetchLoanAndAdvances()
      this.filter.reportType = 1
    },
    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabledText = false
      } else {
        this.isDisabledText = true
        this.filter.financialDate = null
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProductByTypes({
          type1: 'Loan',
          type2: 'Savings',
          permission: reportPermissions.LOAN_AND_ADVANCES_REPORT,
        })
        const { data } = response
        if (data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async handleSubmit(bvModalEvt) {
      if (this.filter.useCurrentFinancialDate === false) {
        bvModalEvt.preventDefault()
        this.$refs.loanAdvanceRules.validate().then(async success => {
          if (success) {
            this.fetchLoanAndAdvances()
          }
        })
      } else {
        this.fetchLoanAndAdvances()
      }
    },
    async fetchLoanAndAdvances() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getLoanAdvances({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isDisabled = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async getReportType(type) {
      if (type) {
        this.filter.reportType = type
        await this.fetchLoanAndAdvances()
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadLoanAdvances({
          search: this.filter,
          page: 1,
          limit: 100000,
          fileExportFormat: 1,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'Loan And Advances.csv')
          this.alertSuccess({ text: 'Your request to download Loan And Advances Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadLoanAdvances({
          search: this.filter,
          page: 1,
          limit: 100000,
          fileExportFormat: 0,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
          FileSaver.saveAs(blob, 'Loan And Advances.xlsx')
          this.alertSuccess({ text: 'Your request to download Loan And Advances Report was successful' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatAmount: toBalance,
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
